import React, { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AvatarSection from '../UserProfile/components/AvatarSection';
import UserDetails from '../UserProfile/components/UserDetails/UserDetails';
import UserPortfolio from '../UserProfile/components/UserPortfolio/UserPortfolio';
import UserRates from '../UserProfile/components/UserRates/UserRates';
import UserReviews from '../UserProfile/components/UserReviews/UserReviews';
import { useParams, useNavigate } from 'react-router-dom';
import GoogleMapsWrapper from '../UserProfile/components/UserDetails/GoogleMapsWrapper';
import { UserDetailsSkeleton } from '../UserProfile/skeletons/UserDetailsSkeleton';
import { AvatarSkeleton } from '../UserProfile/skeletons/AvatarSkeleton';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role='tabpanel' hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

enum PublicProfileTabs {
  ContactInfo = 1,
  Portfolio = 2,
  Rates = 3,
  Reviews = 4,
}

const PublicUserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState<PublicProfileTabs>(
    PublicProfileTabs.ContactInfo,
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleChange = (newValue: PublicProfileTabs) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px',
        gap: '48px',
        minHeight: '100vh',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        paddingTop: '120px',
        paddingBottom: '60px',
        position: 'relative',
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleGoBack}
        sx={{
          position: 'fixed',
          top: 80,
          right: 24,
          zIndex: 1000,
          width: '48px',
          height: '48px',
          background: 'rgba(153, 153, 153, 0.3)',
          backgroundBlendMode: 'darken',
          boxShadow:
            '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
          backdropFilter: 'blur(6px)',
          borderRadius: '64px',
          '&:hover': {
            background: 'rgba(153, 153, 153, 0.4)',
          },
        }}
      >
        <CloseIcon sx={{ color: isDark ? '#D5D5D5' : '#1A1A1A' }} />
      </IconButton>

      {/* Avatar Section Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px',
          gap: '48px',
          width: '956px',
          maxWidth: '95%',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          boxShadow:
            '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
          borderRadius: '64px',
        }}
      >
        {!userId ? <AvatarSkeleton /> : <AvatarSection userId={userId} />}
      </Box>

      {/* Tabs and Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          width: '1152px',
          maxWidth: '95%',
          gap: '0px',
        }}
      >
        {/* Tabs Row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '0px',
            width: '100%',
            height: '48px',
          }}
        >
          {/* General Tab */}
          <Box
            onClick={() => handleChange(PublicProfileTabs.ContactInfo)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 32px',
              gap: '10px',
              flex: '1',
              height: '48px',
              background:
                activeTab === PublicProfileTabs.ContactInfo
                  ? isDark
                    ? '#1A1A1A'
                    : '#E5E5DC'
                  : 'transparent',
              boxShadow:
                activeTab === PublicProfileTabs.ContactInfo
                  ? '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF'
                  : 'none',
              borderRadius: '32px 32px 0px 0px',
              cursor: 'pointer',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background:
                  activeTab === PublicProfileTabs.ContactInfo
                    ? isDark
                      ? '#1A1A1A'
                      : '#E5E5DC'
                    : 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Box
              sx={{
                fontFamily: 'Montserrat',
                fontStyle: 'normal',
                fontWeight: 800,
                fontSize: '20px',
                lineHeight: '160%',
                textAlign: 'center',
                letterSpacing: '0.15px',
                color: isDark ? '#D5D5D5' : '#585858',
                width: '90px',
                whiteSpace: 'nowrap',
              }}
            >
              General
            </Box>
          </Box>

          {/* Portfolio Tab */}
          <Box
            onClick={() => handleChange(PublicProfileTabs.Portfolio)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 32px',
              gap: '10px',
              flex: '1',
              height: '48px',
              background:
                activeTab === PublicProfileTabs.Portfolio
                  ? isDark
                    ? '#1A1A1A'
                    : '#E5E5DC'
                  : 'transparent',
              boxShadow:
                activeTab === PublicProfileTabs.Portfolio
                  ? '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF'
                  : 'none',
              borderRadius: '32px 32px 0px 0px',
              cursor: 'pointer',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background:
                  activeTab === PublicProfileTabs.Portfolio
                    ? isDark
                      ? '#1A1A1A'
                      : '#E5E5DC'
                    : 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Box
              sx={{
                fontFamily: 'Montserrat',
                fontStyle: 'normal',
                fontWeight: 800,
                fontSize: '20px',
                lineHeight: '160%',
                textAlign: 'center',
                letterSpacing: '0.15px',
                color: isDark ? '#D5D5D5' : '#585858',
                width: '95px',
                whiteSpace: 'nowrap',
              }}
            >
              Portfolio
            </Box>
          </Box>

          {/* Rates Tab */}
          <Box
            onClick={() => handleChange(PublicProfileTabs.Rates)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 32px',
              gap: '10px',
              flex: '1',
              height: '48px',
              background:
                activeTab === PublicProfileTabs.Rates
                  ? isDark
                    ? '#1A1A1A'
                    : '#E5E5DC'
                  : 'transparent',
              boxShadow:
                activeTab === PublicProfileTabs.Rates
                  ? '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF'
                  : 'none',
              borderRadius: '32px 32px 0px 0px',
              cursor: 'pointer',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background:
                  activeTab === PublicProfileTabs.Rates
                    ? isDark
                      ? '#1A1A1A'
                      : '#E5E5DC'
                    : 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Box
              sx={{
                fontFamily: 'Montserrat',
                fontStyle: 'normal',
                fontWeight: 800,
                fontSize: '20px',
                lineHeight: '160%',
                textAlign: 'center',
                letterSpacing: '0.15px',
                color: isDark ? '#D5D5D5' : '#585858',
                width: '61px',
                whiteSpace: 'nowrap',
              }}
            >
              Rates
            </Box>
          </Box>

          {/* Reviews Tab */}
          <Box
            onClick={() => handleChange(PublicProfileTabs.Reviews)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 32px',
              gap: '10px',
              flex: '1',
              height: '48px',
              background:
                activeTab === PublicProfileTabs.Reviews
                  ? isDark
                    ? '#1A1A1A'
                    : '#E5E5DC'
                  : 'transparent',
              boxShadow:
                activeTab === PublicProfileTabs.Reviews
                  ? '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF'
                  : 'none',
              borderRadius: '32px 32px 0px 0px',
              cursor: 'pointer',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background:
                  activeTab === PublicProfileTabs.Reviews
                    ? isDark
                      ? '#1A1A1A'
                      : '#E5E5DC'
                    : 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Box
              sx={{
                fontFamily: 'Montserrat',
                fontStyle: 'normal',
                fontWeight: 800,
                fontSize: '20px',
                lineHeight: '160%',
                textAlign: 'center',
                letterSpacing: '0.15px',
                color: isDark ? '#D5D5D5' : '#585858',
                width: '90px',
                whiteSpace: 'nowrap',
              }}
            >
              Reviews
            </Box>
          </Box>
        </Box>

        {/* Content Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '10px',
            width: '100%',
            minHeight: '902px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minHeight: '902px',
              // background: isDark ? '#1A1A1A' : '#E5E5DC',
              boxShadow:
                '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
              borderRadius:
                activeTab === PublicProfileTabs.ContactInfo
                  ? '0px 64px 64px 64px'
                  : activeTab === PublicProfileTabs.Reviews
                    ? '64px 0px 64px 64px'
                    : '64px',
              padding: '48px 24px 24px 24px',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <TabPanel value={activeTab} index={PublicProfileTabs.ContactInfo}>
              <GoogleMapsWrapper fallback={<UserDetailsSkeleton />}>
                {!userId ? (
                  <UserDetailsSkeleton />
                ) : (
                  <UserDetails userId={userId} />
                )}
              </GoogleMapsWrapper>
            </TabPanel>

            <TabPanel value={activeTab} index={PublicProfileTabs.Portfolio}>
              <UserPortfolio userId={userId} />
            </TabPanel>

            <TabPanel value={activeTab} index={PublicProfileTabs.Rates}>
              <UserRates userId={userId} />
            </TabPanel>

            <TabPanel value={activeTab} index={PublicProfileTabs.Reviews}>
              <UserReviews userId={userId} />
            </TabPanel>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicUserProfile;
